'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { storage } from '@bts-web/utils-storage';
import { TradePriceType, OrderOperation } from '../../../orders/types';
import { RegularChevronDown } from '../../../common/svgIcons';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { Tooltip } from '../../../common/components/Tooltip/Tooltip';
import { useIsBelowScreenWidth } from '../../../layouts/utils/useIsBelowScreenWidth';
import { PriceTypeDrawer } from '../../../orders/components/PriceTypeDrawer/PriceTypeDrawer';
import { ButtonBase } from '../../../common/components/ButtonBase/ButtonBase';
import { getAssetTradeUrl } from '../../../route-builders';

interface PriceTypeDrawerButtonProps {
  orderOperation: OrderOperation;
  priceType: TradePriceType;
  assetId: string;
}

const PriceTypeDrawerButton = ({
  orderOperation,
  priceType,
  assetId,
}: PriceTypeDrawerButtonProps) => {
  const { t } = useClientTranslation();

  const router = useRouter();

  const searchParams = useSearchParams();

  const amountSearchParam = searchParams.get('amount') || '';

  const amountValue = isNaN(Number(amountSearchParam)) ? '' : amountSearchParam;

  const { openDrawer, close } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1024);

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  let label = '';

  if (priceType === TradePriceType.STOP) {
    label =
      orderOperation === OrderOperation.SELL
        ? t('stop_order_sell_title')
        : t('stop_order_buy_title');
  } else {
    label = orderOperation === OrderOperation.SELL ? t('sell') : t('buy');
  }

  const onChangeTradeType = (value: TradePriceType) => {
    const redirectUrl =
      value === TradePriceType.STOP
        ? getAssetTradeUrl(assetId, {
            type: 'orderForm',
            orderType: orderOperation,
            amount: amountValue || undefined,
          })
        : getAssetTradeUrl(assetId, {
            type: orderOperation === OrderOperation.SELL ? 'sell' : 'buy',
            trailingSlash: false,
          }) + (amountValue ? `?amount=${amountValue}` : '');

    router.push(redirectUrl);
  };

  const onClick = () => {
    setIsTooltipOpen(false);

    storage.setItem('order-user-state', { opened: true });

    openDrawer(PriceTypeDrawer, {
      title:
        orderOperation === OrderOperation.SELL
          ? t('stop_order_sell_type')
          : t('stop_order_buy_type'),
      position: isMobile ? 'bottom' : 'right',
      selectedOrderOperation: orderOperation,
      priceType,
      onChangeTradeType,
    });
  };

  useEffect(() => {
    // Show tooltip only if not previously seen
    const state = storage.getItem('order-user-state');

    const hasSeenTooltip = state?.opened ?? false;

    if (!hasSeenTooltip) {
      setIsTooltipOpen(true);
    }

    return () => {
      // Mark as seen when component unmounts (navigation away from page)
      if (!storage.getItem('order-user-state')?.opened) {
        storage.setItem('order-user-state', { opened: true });
      }

      close();
    };
  }, []);

  return (
    <Tooltip open={isTooltipOpen} content={t('stop_order_type_tooltip')}>
      <ButtonBase
        noPadding
        className={css({
          borderRadius: '32px',
          paddingInline: 'extra_small_2',
          paddingBlockEnd: 'extra_small_3',
          paddingBlockStart: 'extra_small_3',
        })}
        size="extra_small"
        visual="tertiary"
        withBorderRadius={false}
        onClick={onClick}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: 'extra_small_2',
          })}
        >
          <span>{label}</span>
          <RegularChevronDown size={16} />
        </div>
      </ButtonBase>
    </Tooltip>
  );
};

export { PriceTypeDrawerButton };
